import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { DiditSdk } from "@didit-protocol/sdk-web";
import AppShell from "../components/AppShell";
import AppModal from "../components/AppModal";
import { formatDate, parseToISO } from "../utils/dateUtils";

const Reportar = () => {
  const navigate = useNavigate();
  const [contratos, setContratos] = useState([]);
  const [tiposReporte, setTiposReporte] = useState([]);
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [contratoInfo, setContratoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: "", message: "", type: "info" });

  const [existingReports, setExistingReports] = useState([]);
  const [editingReportId, setEditingReportId] = useState(null);
  const [reportesList, setReportesList] = useState([]);
  const [globalFechaEntrega, setGlobalFechaEntrega] = useState("");
  const [contratoEstadoCuenta, setContratoEstadoCuenta] = useState(null);

  // Requisitos obligatorios: Bien Inmueble registrado + Verificación de Vida en Didit
  const [prerequisitos, setPrerequisitos] = useState(null);
  const [validandoDidit, setValidandoDidit] = useState(false);
  const [diditModalOpen, setDiditModalOpen] = useState(false);

  const NEGATIVE_REPORT_TYPES = ['AR', 'DA', 'SE', 'US', 'OC'];
  const DATE_REQUIRED_REPORT_TYPES = ['AR', 'DA', 'SE', 'US', 'OC', 'CF'];
  const contratoTieneCF = () => existingReports.some((r) => r.TRHTipoReporte === 'CF');
  const contratoBloqueaNegativos = () => contratoTieneCF();
  const isNegativeReportType = (tipo) => NEGATIVE_REPORT_TYPES.includes(tipo);
  const isFechaEntregaRequired = () => reportesList.some((r) => DATE_REQUIRED_REPORT_TYPES.includes(r.TRHTipoReporte));
  const requiereFechaEntrega = (tipo) => DATE_REQUIRED_REPORT_TYPES.includes(tipo);

  const role = localStorage.getItem("role");
  const esAdmin = role === 'ADMINISTRADOR';
  const puedeReportar = esAdmin || Boolean(prerequisitos?.puede_reportar);

  const [form, setForm] = useState({
    TRHTipoReporte: "",
    TRHValorAdeudado: "",
    TRHObservacion: "",
  });

  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [pagoModal, setPagoModal] = useState({ isOpen: false, TRHId: null, TRHValorPagado: "", TRHPobservacion: "", TRHFechaPago: "" });

  const showModal = (title, message, type = "info") => {
    setModal({ isOpen: true, title, message, type });
  };

  const token = localStorage.getItem("token");

  const cargarPrerrequisitos = useCallback(async () => {
    if (!token) return null;
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || ""}/referencias/api/historial/estado_requisitos/`, {
        headers: { Authorization: `Token ${token}` },
      });
      setPrerequisitos(res.data);
      return res.data;
    } catch (err) {
      console.warn("No se pudieron cargar los requisitos de reporte", err);
      const fallback = {
        username: localStorage.getItem("user") || "",
        inmuebles_count: 0,
        didit: { verificado: false },
        puede_reportar: false,
      };
      setPrerequisitos(fallback);
      return fallback;
    }
  }, [token]);

  const handleVerificarDidit = useCallback(async (notificarSilencioso = false) => {
    setValidandoDidit(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL || ""}/referencias/api/historial/didit_verificar/`, {}, {
        headers: { Authorization: `Token ${token}` },
      });
      if (res.data.verificado) {
        setDiditModalOpen(false);
        showModal("Verificación Didit Exitosa", `Prueba de Vida en Didit aprobada exitosamente para el documento ${res.data.documento || localStorage.getItem("user") || ""} (Biometría: ${res.data.score}%). Requisito completado.`, "success");
        await cargarPrerrequisitos();
      } else {
        if (!notificarSilencioso) {
          showModal("Verificación Didit Pendiente", res.data.mensaje || "Aún no se ha detectado una sesión completada y aprobada para su documento en Didit.", "info");
        }
      }
    } catch (err) {
      if (!notificarSilencioso) {
        showModal("Error Didit", "No se pudo sincronizar la verificación con Didit.", "error");
      }
    } finally {
      setValidandoDidit(false);
    }
  }, [token, cargarPrerrequisitos]);

  const iniciarVerificacionDidit = useCallback(async () => {
    let targetUrl = prerequisitos?.didit?.verification_url || "https://verify.didit.me/u/wqvI0FviQJCJeVLO81E_Hw";

    // Intentar obtener URL directa de sesión desde el backend
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/referencias/api/historial/didit_iniciar/`,
        {},
        { headers: { Authorization: `Token ${token}` } }
      );
      if (res.data && res.data.url) {
        targetUrl = res.data.url;
      }
    } catch (e) {
      console.warn("Fallo didit_iniciar, usando fallback UniLink:", e);
    }

    // Configurar callbacks del SDK oficial de Didit
    DiditSdk.shared.onComplete = async (result) => {
      console.log("[Didit SDK] Resultado de verificación:", result);
      if (result && result.type === "completed") {
        showModal(
          "Verificación Didit Enviada",
          "Ha completado el proceso de verificación en Didit. Sincronizando estado con el sistema...",
          "info"
        );
        await handleVerificarDidit(false);
      } else if (result && result.type === "cancelled") {
        showModal("Verificación Didit", "El proceso de verificación en Didit fue cerrado o cancelado.", "info");
      }
    };

    DiditSdk.shared.onEvent = (event) => {
      console.log("[Didit SDK] Evento:", event);
      if (event?.type === "didit:completed" || event?.type === "didit:verification_submitted") {
        handleVerificarDidit(true);
      }
    };

    try {
      DiditSdk.shared.startVerification({
        url: targetUrl,
        configuration: {
          loggingEnabled: true,
          showCloseButton: true,
          showExitConfirmation: true,
          closeModalOnComplete: true,
          zIndex: 99999,
          defaultDocumentCamera: "back",
          defaultLivenessCamera: "front",
          showDocumentCameraSwitchButton: true,
          showLivenessCameraSwitchButton: true,
        },
      });
    } catch (err) {
      console.error("[Didit SDK] Error iniciando verificación SDK:", err);
      // Fallback a modal integrado en caso de error
      setDiditModalOpen(true);
    }
  }, [prerequisitos, token, handleVerificarDidit]);

  const renderRequisitosVerificacion = () => {
    if (!prerequisitos) {
      return (
        <section className="app-surface" style={{ marginBottom: '20px', padding: '16px' }}>
          <p style={{ margin: 0, color: '#64748b' }}>Cargando estado de requisitos de verificación...</p>
        </section>
      );
    }
    const esAdmin = role === 'ADMINISTRADOR';
    const tieneInmuebles = prerequisitos.inmuebles_count > 0 || esAdmin;
    const tieneDidit = Boolean(prerequisitos.didit?.verificado || esAdmin);
    const cumpleRequisitos = tieneInmuebles && tieneDidit;

    return (
      <section
        className="app-surface"
        style={{
          borderLeft: `5px solid ${cumpleRequisitos ? '#10b981' : '#dc2626'}`,
          background: cumpleRequisitos ? '#f0fdf4' : '#fff5f5',
          marginBottom: '20px',
        }}
      >
        <div className="app-section-title">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <h2 style={{ color: cumpleRequisitos ? '#166534' : '#991b1b', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <span>{cumpleRequisitos ? '🛡️' : '⚠️'}</span> Requisitos Obligatorios de Verificación para Reportar
            </h2>
            <span
              style={{
                fontSize: '12px',
                fontWeight: '700',
                padding: '4px 10px',
                borderRadius: '12px',
                backgroundColor: cumpleRequisitos ? '#d1fae5' : '#fee2e2',
                color: cumpleRequisitos ? '#065f46' : '#991b1b',
              }}
            >
              {cumpleRequisitos ? '✓ Requisitos Completados' : '❌ Requisitos Pendientes'}
            </span>
          </div>
          <p style={{ color: cumpleRequisitos ? '#15803d' : '#7f1d1d', marginTop: '6px' }}>
            {cumpleRequisitos
              ? "Usted cumple con los requisitos necesarios para registrar y publicar reportes de novedades en el sistema."
              : "Para garantizar la autenticidad e integridad del sistema, antes de realizar cualquier reporte debe cumplir con las siguientes dos condiciones obligatorias:"}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '16px' }}>
          {/* Requisito A: Bien Inmueble */}
          <div style={{ padding: '16px', borderRadius: '8px', background: '#ffffff', border: `2px solid ${tieneInmuebles ? '#10b981' : '#ef4444'}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong style={{ fontSize: '14px', color: '#1e293b' }}>a. Bien Inmueble registrado</strong>
              {tieneInmuebles ? (
                <span style={{ backgroundColor: '#d1fae5', color: '#065f46', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                  ✓ Cumplido {esAdmin ? '(Administrador)' : `(${prerequisitos.inmuebles_count})`}
                </span>
              ) : (
                <span style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>❌ Pendiente</span>
              )}
            </div>
            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px 0' }}>
              {tieneInmuebles
                ? `Cuenta con ${prerequisitos.inmuebles_count || 1} propiedad(es) registrada(s) en el sistema${contratoInfo ? ` (Inmueble del contrato: Matrícula ${contratoInfo.TBNoMatricula})` : ""}.`
                : "Debe tener al menos un Bien Inmueble registrado a su nombre o en su organización."}
            </p>
            {!tieneInmuebles && (
              <button
                type="button"
                className="app-button app-button--primary"
                style={{ width: '100%', fontSize: '13px' }}
                onClick={() => navigate("/inmuebles/administrar")}
              >
                + Registrar Bien Inmueble
              </button>
            )}
          </div>

          {/* Requisito B: Didit Liveness */}
          <div style={{ padding: '16px', borderRadius: '8px', background: '#ffffff', border: `2px solid ${tieneDidit ? '#10b981' : '#ef4444'}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong style={{ fontSize: '14px', color: '#1e293b' }}>b. Verificación de Vida en Didit</strong>
              {tieneDidit ? (
                <span style={{ backgroundColor: '#d1fae5', color: '#065f46', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                  ✓ Aprobada {esAdmin ? '(Administrador)' : `(${prerequisitos.didit?.score || 99.62}%)`}
                </span>
              ) : (
                <span style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>❌ Pendiente</span>
              )}
            </div>
            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 4px 0' }}>
              Prueba biométrica de vida pasiva con Didit directamente en la aplicación.
            </p>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '12px' }}>
              Clave API: <code>REFERENCIATE_BACKEND</code> | Documento: <strong>{localStorage.getItem("user") || "—"}</strong>
            </div>
            {!tieneDidit ? (
              <div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="app-button app-button--primary"
                    style={{ flex: 1, fontSize: '13px', whiteSpace: 'nowrap' }}
                    onClick={iniciarVerificacionDidit}
                  >
                    📸 Realizar Verificación en Didit
                  </button>
                  <button
                    type="button"
                    className="app-button app-button--secondary"
                    style={{ fontSize: '13px' }}
                    onClick={() => handleVerificarDidit(false)}
                    disabled={validandoDidit}
                    title="Sincronizar estado con Didit"
                  >
                    {validandoDidit ? "..." : "🔄 Sincronizar"}
                  </button>
                </div>
                <div style={{ marginTop: '6px', textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => setDiditModalOpen(true)}
                    style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Modo panel / ventana alternativa
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: '#166534', background: '#f0fdf4', padding: '8px 10px', borderRadius: '6px', border: '1px solid #bbf7d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Sesión Didit: <code>{prerequisitos.didit?.session_id ? prerequisitos.didit.session_id.substring(0, 16) + '...' : 'Activa / Verificada'}</code></span>
                <button
                  type="button"
                  onClick={iniciarVerificacionDidit}
                  style={{ background: 'transparent', border: 'none', color: '#0284c7', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', fontWeight: '600' }}
                >
                  Ver interfaz Didit
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  useEffect(() => {
    if (!token) {
      showModal("Atención", "No estas logueado", "error");
      navigate("/login");
      return;
    }

    cargarPrerrequisitos();

    Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL || ""}/api/contratoarriendo/`, {
        headers: { Authorization: `Token ${token}` },
      }),
      axios.get(`${process.env.REACT_APP_API_URL || ""}/referencias/api/tiporeporte/`, {
        headers: { Authorization: `Token ${token}` },
      }),
    ])
      .then(([resContratos, resTipos]) => {
        const dataContratos = Array.isArray(resContratos.data) ? resContratos.data : resContratos.data.results || [];
        // REGLA: Solo se puede hacer reportes sobre Contratos Finalizados y no sobre Contratos Vigentes
        const contratosFinalizados = dataContratos.filter((c) => c.TCAFechaEntregaInmueble !== null);
        setContratos(contratosFinalizados);
        setTiposReporte(resTipos.data);
        setLoading(false);
      })
      .catch((err) => {
        const errorMsg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
        setError(`Error al cargar datos: ${errorMsg}`);
        setLoading(false);
      });
  }, [token, navigate, cargarPrerrequisitos]);

  // Polling automático mientras el modal de Didit está abierto
  useEffect(() => {
    let timer = null;
    if (diditModalOpen) {
      timer = setInterval(async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_URL || ""}/referencias/api/historial/didit_status/`, {
            headers: { Authorization: `Token ${token}` },
          });
          if (res.data && res.data.verificado) {
            setDiditModalOpen(false);
            showModal(
              "Verificación Didit Exitosa",
              `Prueba de Vida en Didit aprobada exitosamente (Biometría: ${res.data.score}%). Requisito completado.`,
              "success"
            );
            cargarPrerrequisitos();
          }
        } catch (e) {
          // ignore polling error
        }
      }, 7000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [diditModalOpen, token, cargarPrerrequisitos]);

  // Escuchar eventos postMessage de Didit en el iframe
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.origin && (e.origin.includes("didit.me") || e.origin.includes("verify.didit.me"))) {
        handleVerificarDidit(true);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleVerificarDidit]);

  const loadExistingReports = (contratoId) => {
    axios.get(`${process.env.REACT_APP_API_URL || ""}/referencias/api/historial/?TCAIDContrato=${contratoId}`, {
      headers: { Authorization: `Token ${token}` },
    }).then(res => {
      setExistingReports(res.data);
    }).catch(err => {
      console.error("Error cargando reportes existentes", err);
    });
  };

  const duracionTipoTexto = (tipo) => {
    const labels = {
      DD: "Días",
      MM: "Meses",
      AA: "Años",
    };
    return labels[tipo] || tipo;
  };

  const handleContratoChange = (e) => {
    const contratoId = parseInt(e.target.value, 10);
    const contrato = contratos.find((c) => c.TCAIDContrato === contratoId);
    setSelectedContrato(contratoId);
    setContratoInfo(contrato);
    setReportesList([]);
    setGlobalFechaEntrega("");
    setEditingReportId(null);
    setForm({ TRHTipoReporte: "", TRHValorAdeudado: "", TRHObservacion: "" });
    if (contratoId) {
      loadExistingReports(contratoId);
      // Consultar estado de cuenta del contrato para predeterminar el saldo pendiente de canon
      axios.get(`${process.env.REACT_APP_API_URL || ""}/api/contratos/${contratoId}/estado-cuenta/`, {
        headers: { Authorization: `Token ${token}` },
      }).then(res => {
        setContratoEstadoCuenta(res.data);
      }).catch(err => {
        console.error("Error al obtener estado de cuenta", err);
        setContratoEstadoCuenta(contrato?.estado_cuenta || null);
      });
    } else {
      setExistingReports([]);
      setContratoEstadoCuenta(null);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const upperValue = value.toUpperCase();

    if (name === 'TRHTipoReporte' && contratoBloqueaNegativos() && isNegativeReportType(upperValue)) {
      showModal(
        "No permitido",
        "No se pueden ingresar reportes negativos sobre un contrato que ya cuenta con reporte finalizado sin deuda (CF).",
        "error"
      );
      setForm({ ...form, TRHTipoReporte: "" });
      return;
    }

    // Validación de reportes duplicados o ya existentes sobre el contrato finalizado
    if (name === 'TRHTipoReporte' && existingReports.some((r) => r.TRHTipoReporte === upperValue)) {
      showModal(
        "No permitido",
        "No puede haber reportes duplicados sobre un Contrato Finalizado",
        "error"
      );
      setForm({ ...form, TRHTipoReporte: "" });
      return;
    }

    // Regla: Cuando se reporte tipo AR (Arriendo y Cuota de Administración), tomar el SALDO PENDIENTE automáticamente
    if (name === 'TRHTipoReporte') {
      let nuevoValorAdeudado = form.TRHValorAdeudado;
      if (upperValue === 'AR') {
        const saldoDisponible = contratoEstadoCuenta?.saldo !== undefined
          ? contratoEstadoCuenta.saldo
          : (contratoInfo?.estado_cuenta?.saldo !== undefined ? contratoInfo.estado_cuenta.saldo : null);

        if (saldoDisponible !== null) {
          nuevoValorAdeudado = saldoDisponible.toString();
        }
      }
      setForm({ ...form, TRHTipoReporte: upperValue, TRHValorAdeudado: nuevoValorAdeudado });
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const handleEditReport = (report) => {
    setEditingReportId(report.TRHId);
    setForm({
      TRHTipoReporte: report.TRHTipoReporte,
      TRHValorAdeudado: report.TRHValorAdeudado || "",
      TRHObservacion: report.TRHObservacion || "",
    });
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handleDeleteReport = (reportId) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este reporte?")) {
      axios.delete(`${process.env.REACT_APP_API_URL || ""}/referencias/api/historial/${reportId}/`, {
        headers: { Authorization: `Token ${token}` }
      }).then(() => {
        showModal("Exito", "Reporte eliminado correctamente", "success");
        loadExistingReports(selectedContrato);
      }).catch(err => {
        const errorMsg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
        showModal("Error", `Error al eliminar: ${errorMsg}`, "error");
      });
    }
  };

  const addToList = (e) => {
    e.preventDefault();

    if (!puedeReportar) {
      showModal(
        "Requisitos Incompletos",
        "No puede añadir reportes hasta completar los requisitos obligatorios: Tener un Bien Inmueble registrado y Verificación de Vida aprobada en Didit.",
        "error"
      );
      return;
    }

    if (!selectedContrato) {
      showModal("Atención", "Debes seleccionar un contrato", "error");
      return;
    }

    if (!form.TRHTipoReporte) {
      showModal("Atención", "Debes seleccionar un tipo de reporte", "error");
      return;
    }

    if (isNegativeReportType(form.TRHTipoReporte) && contratoBloqueaNegativos()) {
      showModal(
        "No permitido",
        "No se pueden ingresar reportes negativos sobre un contrato con reporte finalizado sin deuda (CF).",
        "error"
      );
      return;
    }

    // Regla de negocio: No puede haber reportes duplicados sobre un Contrato Finalizado
    if (existingReports.some((r) => r.TRHTipoReporte === form.TRHTipoReporte)) {
      showModal("No permitido", "No puede haber reportes duplicados sobre un Contrato Finalizado", "error");
      return;
    }

    if (reportesList.some((r) => r.TRHTipoReporte === form.TRHTipoReporte)) {
      showModal("No permitido", "No puede haber reportes duplicados sobre un Contrato Finalizado", "error");
      return;
    }

    if (editingReportId) {
      if (requiereFechaEntrega(form.TRHTipoReporte) && !globalFechaEntrega && !contratoInfo?.TCAFechaEntregaInmueble) {
        showModal("Atención", "Debes ingresar la Fecha de Entrega del Inmueble para actualizar este reporte.", "error");
        return;
      }

      setSubmitting(true);
      axios.put(`${process.env.REACT_APP_API_URL || ""}/referencias/api/historial/${editingReportId}/`, {
        TCAIDContrato: selectedContrato,
        TRHTipoReporte: form.TRHTipoReporte,
        TRHValorAdeudado: form.TRHValorAdeudado ? parseFloat(form.TRHValorAdeudado) : null,
        TRHObservacion: form.TRHObservacion,
        ...(requiereFechaEntrega(form.TRHTipoReporte) && globalFechaEntrega ? { fecha_entrega_inmueble: globalFechaEntrega } : {}),
      }, {
        headers: { Authorization: `Token ${token}`, "Content-Type": "application/json" }
      }).then(() => {
        showModal("Exito", "Reporte actualizado correctamente", "success");
        setForm({ TRHTipoReporte: "", TRHValorAdeudado: "", TRHObservacion: "" });
        setEditingReportId(null);
        loadExistingReports(selectedContrato);
      }).catch(err => {
        const errorMsg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
        showModal("Error", `Error al actualizar: ${errorMsg}`, "error");
      }).finally(() => {
        setSubmitting(false);
      });
      return;
    }

    setReportesList([...reportesList, { ...form }]);
    setForm({ TRHTipoReporte: "", TRHValorAdeudado: "", TRHObservacion: "" });
  };

  const cancelEdit = () => {
    setEditingReportId(null);
    setForm({ TRHTipoReporte: "", TRHValorAdeudado: "", TRHObservacion: "" });
  };

  const removeFromList = (tipo) => {
    setReportesList(reportesList.filter((r) => r.TRHTipoReporte !== tipo));
  };

  const isGlobalFechaRequired = reportesList.some((r) =>
    DATE_REQUIRED_REPORT_TYPES.includes(r.TRHTipoReporte)
  );

  const executeSubmitAll = () => {
    setSubmitting(true);

    const payload = reportesList.map((r) => ({
      TCAIDContrato: selectedContrato,
      TRHTipoReporte: r.TRHTipoReporte,
      TRHValorAdeudado: r.TRHValorAdeudado ? parseFloat(r.TRHValorAdeudado) : null,
      TRHObservacion: r.TRHObservacion,
      ...(isGlobalFechaRequired && { fecha_entrega_inmueble: globalFechaEntrega }),
    }));

    axios
      .post(`${process.env.REACT_APP_API_URL || ""}/referencias/api/historial/`, payload, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        const d = new Date();
        d.setDate(d.getDate() + 20);
        const fechaVigenciaStr = d.toISOString().split("T")[0];
        showModal(
          "Reporte Ingresado y Notificado",
          `Reportes ingresados exitosamente. Se ha enviado una notificación por SMS a los números de celular de los Arrendatarios y Codeudores del contrato, programando la vigencia y aplicación de estos reportes para el ${formatDate(fechaVigenciaStr)} (20 días después de su ingreso).`,
          "success"
        );
        setReportesList([]);
        loadExistingReports(selectedContrato);
      })
      .catch((err) => {
        const dataErr = err.response?.data;
        const errorMsg = typeof dataErr === "object" ? JSON.stringify(dataErr) : (err.message || "");
        if (
          errorMsg.includes("No puede haber reportes duplicados sobre un Contrato Finalizado") ||
          errorMsg.includes("duplicado") ||
          errorMsg.includes("unique") ||
          errorMsg.includes("Ya existe")
        ) {
          showModal("No permitido", "No puede haber reportes duplicados sobre un Contrato Finalizado", "error");
        } else {
          showModal("Error", `Error al guardar reporte: ${errorMsg}`, "error");
        }
      })
      .finally(() => {
        setSubmitting(false);
        setConfirmModal({ isOpen: false });
      });
  };

  const handleSubmitAll = () => {
    if (!puedeReportar) {
      showModal(
        "Requisitos Incompletos",
        "No puede enviar reportes hasta completar los requisitos obligatorios: Tener un Bien Inmueble registrado y Verificación de Vida aprobada en Didit.",
        "error"
      );
      return;
    }

    if (reportesList.length === 0) {
      showModal("Atención", "Debes agregar al menos un reporte a la lista.", "error");
      return;
    }

    if (isFechaEntregaRequired() && !globalFechaEntrega && !contratoInfo?.TCAFechaEntregaInmueble) {
      showModal("Atención", "Debes ingresar la Fecha de Entrega del Inmueble para guardar los reportes.", "error");
      return;
    }

    const hasNegative = reportesList.some((r) => ["AR", "DA", "OC", "SE", "US"].includes(r.TRHTipoReporte));
    if (hasNegative && role === "ARRENDADOR") {
      setConfirmModal({ isOpen: true });
    } else {
      executeSubmitAll();
    }
  };

  const handleOpenPagoModal = (report) => {
    setPagoModal({
      isOpen: true,
      TRHId: report.TRHId,
      TCAIDContrato: report.TCAIDContrato,
      TRHValorPagado: "",
      TRHPobservacion: "",
      TRHFechaPago: new Date().toISOString().split("T")[0],
      saldo: report.saldo !== undefined ? report.saldo : report.TRHValorAdeudado
    });
  };

  const handlePagoChange = (e) => {
    const { name, value } = e.target;
    setPagoModal({ ...pagoModal, [name]: value });
  };

  const handleSubmitPago = (e) => {
    e.preventDefault();
    setSubmitting(true);
    axios.post(`${process.env.REACT_APP_API_URL || ""}/referencias/api/historial/${pagoModal.TRHId}/pagar/`, {
      TRHValorPagado: parseFloat(pagoModal.TRHValorPagado),
      TRHPobservacion: pagoModal.TRHPobservacion,
      TRHFechaPago: parseToISO(pagoModal.TRHFechaPago)
    }, {
      headers: { Authorization: `Token ${token}`, "Content-Type": "application/json" }
    }).then(() => {
      showModal("Exito", "Pago registrado correctamente", "success");
      setPagoModal({ isOpen: false, TRHId: null, TRHValorPagado: "", TRHPobservacion: "", TRHFechaPago: "" });
      loadExistingReports(selectedContrato);
    }).catch((err) => {
      const errorMsg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
      showModal("Error", `Error al registrar pago: ${errorMsg}`, "error");
    }).finally(() => {
      setSubmitting(false);
    });
  };

  return (
    <AppShell
      eyebrow=""
      title="Reportar referencia"
      subtitle="Selecciona tus contratos y reporta las novedades positivas o negativas en su finalizacion."
    >
      {loading && <div className="app-message app-message--info">Cargando datos del formulario...</div>}

      {error && (
        <section className="app-surface">
          <h2>Error al cargar reportes</h2>
          <div className="app-message app-message--error">{error}</div>
          <div className="app-actions">
            <button className="app-button app-button--secondary" type="button" onClick={() => navigate("/referencias")}>
              Volver al menu
            </button>
            <button className="app-button app-button--primary" type="button" onClick={() => window.location.reload()}>
              Reintentar
            </button>
          </div>
        </section>
      )}

      {!loading && !error && contratos.length === 0 && (
        <section className="app-surface">
          <h2>No hay contratos finalizados disponibles</h2>
          <p>
            En este módulo <strong>solo se pueden registrar reportes sobre Contratos Finalizados</strong> (con Fecha de Entrega del Inmueble registrada). Los contratos vigentes no admiten reportes.
          </p>
          <div className="app-actions">
            <button className="app-button app-button--secondary" type="button" onClick={() => navigate("/contratos")}>
              Ver lista de contratos
            </button>
          </div>
        </section>
      )}

      {/* Requisitos de Verificación cuando aún NO se ha seleccionado un contrato */}
      {!selectedContrato && renderRequisitosVerificacion()}

      {!loading && !error && contratos.length > 0 && (
        <div className="app-content">
          <section className="app-surface">
            <div className="app-section-title">
              <h2>Selección del contrato finalizado</h2>
              <p>Escoge el contrato finalizado que deseas reportar y luego completa la novedad.</p>
            </div>

            <div className="app-form-grid">
              <div className="app-field">
                <label htmlFor="contrato-select">Contrato Finalizado</label>
                <select
                  id="contrato-select"
                  value={selectedContrato || ""}
                  onChange={handleContratoChange}
                  required
                  className="app-select"
                >
                  <option value="">-- Selecciona un contrato finalizado --</option>
                  {contratos.map((contrato) => (
                    <option key={contrato.TCAIDContrato} value={contrato.TCAIDContrato}>
                      Contrato No. {contrato.TCAIDContrato} | Matrícula: {contrato.TBNoMatricula} | {contrato.TBDireccion} | Entregado: {formatDate(contrato.TCAFechaEntregaInmueble)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {contratoInfo && (
            <section className="app-surface">
              <div className="app-section-title">
                <h2>Resumen del contrato</h2>
              </div>
              {contratoBloqueaNegativos() && (
                <div className="app-message app-message--warning">
                  Este contrato ya cuenta con un reporte de Contrato Finalizado sin deuda (CF). No se permiten reportes negativos adicionales.
                </div>
              )}
              <div className="app-grid app-grid--2">
                <div><strong>ID Contrato:</strong> {contratoInfo.TCAIDContrato}</div>
                <div><strong>Matricula:</strong> {contratoInfo.TBNoMatricula}</div>
                <div><strong>Direccion:</strong> {contratoInfo.TBDireccion}</div>
                <div><strong>Valor Canon:</strong> ${parseFloat(contratoInfo.TCAValorCanonContrato).toLocaleString()}</div>
                <div><strong>Fecha Contrato:</strong> {formatDate(contratoInfo.TCAFechaContrato)}</div>
                <div><strong>Fecha Inicio:</strong> {formatDate(contratoInfo.TCAFechaInicioContrato)}</div>
                <div><strong>Fecha Entrega:</strong> {formatDate(contratoInfo.TCAFechaEntregaInmueble) || "No registrada"}</div>
                <div><strong>Duracion:</strong> {contratoInfo.TCADuracionContrato} {duracionTipoTexto(contratoInfo.TCATipoDuracion)}</div>
              </div>

              {/* Arrendatarios y Codeudores del Contrato */}
              <div style={{ marginTop: "16px", borderTop: "1px solid #e2e8f0", paddingTop: "14px" }}>
                <h3 style={{ margin: "0 0 10px 0", fontSize: "1rem", color: "#0f172a" }}>
                  👥 Arrendatarios y Codeudores del Contrato
                </h3>
                {contratoInfo.personas &&
                contratoInfo.personas.filter((p) =>
                  ["ARRENDATARIO", "CODEUDOR"].includes(p.TCARTipoParticipacion)
                ).length > 0 ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: "12px",
                    }}
                  >
                    {contratoInfo.personas
                      .filter((p) =>
                        ["ARRENDATARIO", "CODEUDOR"].includes(p.TCARTipoParticipacion)
                      )
                      .map((p, idx) => {
                        const nombre =
                          p.nombre_completo ||
                          [p.nombres, p.apellidos].filter(Boolean).join(" ");
                        return (
                          <div
                            key={idx}
                            style={{
                              background: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              padding: "12px 14px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                fontSize: "0.75rem",
                                fontWeight: "700",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                background:
                                  p.TCARTipoParticipacion === "ARRENDATARIO"
                                    ? "#e0f2fe"
                                    : "#fef3c7",
                                color:
                                  p.TCARTipoParticipacion === "ARRENDATARIO"
                                    ? "#0369a1"
                                    : "#b45309",
                                marginBottom: "6px",
                              }}
                            >
                              {p.TCARTipoParticipacion}
                            </span>
                            <div
                              style={{
                                fontSize: "0.95rem",
                                fontWeight: "700",
                                color: "#0f172a",
                                marginBottom: "4px",
                              }}
                            >
                              <strong>Nombre:</strong> {nombre || "Nombre no registrado"}
                            </div>
                            <div style={{ fontSize: "0.85rem", color: "#475569" }}>
                              <strong>Número de Documento:</strong> {p.TPTipoDocumento ? `${p.TPTipoDocumento} ` : ""}{p.TPNoDocumento}
                            </div>
                            {p.celular && (
                              <div
                                style={{
                                  fontSize: "0.85rem",
                                  color: "#166534",
                                  marginTop: "3px",
                                }}
                              >
                                <strong>Celular / SMS:</strong> {p.celular}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div style={{ color: "#64748b", fontSize: "0.85rem" }}>
                    No se encontraron Arrendatarios o Codeudores asociados a este contrato.
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Requisitos de Verificación al seleccionar un Contrato Finalizado */}
          {selectedContrato && renderRequisitosVerificacion()}

          {selectedContrato && existingReports.length > 0 && (
            <section className="app-surface">
              <div className="app-section-title">
                <h2>Reportes Existentes ({existingReports.length})</h2>
              </div>
              <div className="wizard-choice-list" style={{ marginBottom: '20px' }}>
                {existingReports.map((r) => {
                  const valorAdeudado = Number(r.TRHValorAdeudado || 0);
                  const totalPagado = Number(r.total_pagado || 0);
                  const currentSaldo = (Number(r.saldo) === 0 && totalPagado === 0 && valorAdeudado > 0) 
                                        ? valorAdeudado 
                                        : Number(r.saldo || 0);
                  return (
                  <div key={r.TRHId} className="wizard-choice">
                    <div>
                      <strong>{r.TRHTipoReporte} - {r.TRHTipoReporte_nombre}</strong>
                      {r.TRHValorAdeudado && <small style={{ display: 'block' }}>Valor Adeudado Inicial: ${r.TRHValorAdeudado}</small>}
                      {r.total_pagado !== undefined && <small style={{ display: 'block' }}>Total Pagado: ${r.total_pagado}</small>}
                      <small style={{ display: 'block', color: currentSaldo > 0 ? 'red' : 'green' }}>Saldo: ${currentSaldo}</small>
                      {r.TRHFechaVigencia && (
                        <small style={{ display: 'block', color: '#0369a1', marginTop: '2px', fontWeight: '600' }}>
                          📅 Vigencia y Aplicación: {formatDate(r.TRHFechaVigencia)} {new Date(r.TRHFechaVigencia + "T00:00:00") > new Date() ? "(Programado: En preaviso de 20 días)" : "(Vigente y Aplicado)"}
                        </small>
                      )}
                      {r.TRHObservacion && <small style={{ display: 'block', marginTop: '4px' }}>Obs: {r.TRHObservacion}</small>}
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>
                      {["AR", "DA", "OC", "SE", "US"].includes(r.TRHTipoReporte) && (
                        <button
                          className="app-button app-button--primary"
                          type="button"
                          onClick={() => handleOpenPagoModal({...r, saldo: currentSaldo})}
                          disabled={currentSaldo <= 0}
                        >
                          Registrar Pago
                        </button>
                      )}
                      {role !== "ARRENDADOR" && (
                        <>
                          <button className="app-button app-button--secondary" type="button" onClick={() => handleEditReport(r)}>
                            Modificar
                          </button>
                          <button className="app-button app-button--danger" type="button" onClick={() => handleDeleteReport(r.TRHId)}>
                            Eliminar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )})}
              </div>
            </section>
          )}

          {selectedContrato && !contratoBloqueaNegativos() && !puedeReportar && (
            <section className="app-surface" style={{ borderLeft: '5px solid #dc2626', background: '#fff5f5', marginBottom: '20px' }}>
              <div className="app-section-title">
                <h2 style={{ color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>⛔</span> Registro de Novedades Bloqueado
                </h2>
                <p style={{ color: '#7f1d1d', margin: '6px 0 0 0' }}>
                  Para poder añadir y registrar reportes sobre el contrato No. {contratoInfo?.TCAIDContrato}, debe completar los <strong>Requisitos de Verificación Obligatorios</strong> mostrados arriba (Bien Raíz registrado y Verificación de Vida en Didit aprobada).
                </p>
              </div>
            </section>
          )}

          {selectedContrato && !contratoBloqueaNegativos() && puedeReportar && (
            <section className="app-surface">
              <div className="app-section-title">
                <h2>{editingReportId ? "Modificar reporte" : "Añadir reporte a la lista"}</h2>
              </div>

              <form onSubmit={addToList} className="app-form-grid" autoComplete="off">
                <div className="app-field">
                  <label htmlFor="tipo-reporte">Tipo de reporte</label>
                  <select
                    id="tipo-reporte"
                    name="TRHTipoReporte"
                    value={form.TRHTipoReporte}
                    onChange={handleFormChange}
                    required
                    className="app-select"
                  >
                    <option value="">-- Selecciona un tipo de reporte --</option>
                    {tiposReporte.map((tipo) => {
                      const disabled = contratoBloqueaNegativos() && isNegativeReportType(tipo.TRHTipoReporte);
                      return (
                        <option key={tipo.TRHTipoReporte} value={tipo.TRHTipoReporte} disabled={disabled}>
                          {tipo.TRHTipoReporte} - {tipo.TRDescripcion}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="app-field">
                  <label htmlFor="valor-adeudado">Valor adeudado</label>
                  <input
                    id="valor-adeudado"
                    type="number"
                    name="TRHValorAdeudado"
                    placeholder="Ingrese el valor adeudado"
                    value={form.TRHValorAdeudado}
                    onChange={handleFormChange}
                    step="0.01"
                    min="0"
                    className="app-input"
                  />
                  {form.TRHTipoReporte === 'AR' && (
                    <small style={{ color: "#0284c7", display: "block", marginTop: "4px" }}>
                      ℹ️ Saldo pendiente tomado automáticamente del Administrador de Pagos {contratoEstadoCuenta?.saldo !== undefined ? `($${new Intl.NumberFormat('es-CO').format(contratoEstadoCuenta.saldo)} COP)` : ""}. Puede modificar este valor si lo requiere.
                    </small>
                  )}
                </div>

                {editingReportId && requiereFechaEntrega(form.TRHTipoReporte) && !contratoInfo?.TCAFechaEntregaInmueble && (
                  <div className="app-field" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="global-fecha-entrega">
                      <strong>Fecha de Entrega del Inmueble (dd/mm/aaaa)</strong>
                    </label>
                    <input
                      id="global-fecha-entrega"
                      type="date"
                      value={globalFechaEntrega}
                      onChange={(e) => setGlobalFechaEntrega(e.target.value)}
                      className="app-input"
                    />
                    <small>Debes registrar la Fecha de Entrega para modificar este reporte.</small>
                  </div>
                )}

                <div className="app-field" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="observacion">Observacion</label>
                  <textarea
                    id="observacion"
                    name="TRHObservacion"
                    placeholder="Ingrese observaciones adicionales"
                    value={form.TRHObservacion}
                    onChange={handleFormChange}
                    rows="4"
                    className="app-textarea"
                  />
                </div>

                <div className="app-actions" style={{ gridColumn: "1 / -1" }}>
                  <button className="app-button app-button--secondary" type="submit">
                    {editingReportId ? "Guardar Cambios" : "Añadir a la lista"}
                  </button>
                  {editingReportId && (
                    <button className="app-button app-button--danger" type="button" onClick={cancelEdit}>
                      Cancelar Edición
                    </button>
                  )}
                </div>
              </form>
            </section>
          )}

          {selectedContrato && !contratoBloqueaNegativos() && puedeReportar && (
            <section className="app-surface">
              <div className="app-section-title">
                <h2>Reportes por Guardar ({reportesList.length})</h2>
              </div>
              
              {reportesList.length === 0 ? (
                <div className="app-empty-state">
                  <strong>Aun no has agregado reportes a la lista</strong>
                  <p>Configura un reporte y presiona "Añadir a la lista" para empezar.</p>
                </div>
              ) : (
                <div className="wizard-choice-list" style={{ marginBottom: '20px' }}>
                  {reportesList.map((r, index) => (
                    <div key={index} className="wizard-choice">
                      <div>
                        <strong>{r.TRHTipoReporte}</strong>
                        {r.TRHValorAdeudado && <small>Valor Adeudado: ${r.TRHValorAdeudado}</small>}
                        {r.TRHObservacion && <small>Obs: {r.TRHObservacion}</small>}
                      </div>
                      <button className="app-button app-button--danger" type="button" onClick={() => removeFromList(r.TRHTipoReporte)}>
                        Quitar
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {isGlobalFechaRequired && !contratoInfo?.TCAFechaEntregaInmueble && reportesList.length > 0 && (
                <div className="app-form-grid" style={{ marginBottom: "20px", padding: "16px", backgroundColor: "#fdf8e4", borderRadius: "8px", border: "1px solid #f1c40f" }}>
                  <div className="app-field" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="global-fecha-entrega">
                      <strong>Fecha de Entrega del Inmueble (dd/mm/aaaa) (*)</strong>
                    </label>
                    <p style={{ margin: "4px 0 12px 0", fontSize: "14px", color: "#666" }}>
                      Los tipos de reporte seleccionados requieren que confirmes la fecha en la que se entregó el bien inmueble.
                    </p>
                    <input
                      id="global-fecha-entrega"
                      type="date"
                      value={globalFechaEntrega}
                      onChange={(e) => setGlobalFechaEntrega(e.target.value)}
                      required
                      className="app-input"
                    />
                  </div>
                </div>
              )}

              <div className="app-actions">
                <button className="app-button app-button--primary" type="button" disabled={submitting || reportesList.length === 0} onClick={handleSubmitAll}>
                  {submitting ? "Guardando todos..." : "Guardar todos los reportes"}
                </button>
                <button className="app-button app-button--secondary" type="button" onClick={() => navigate("/referencias")}>
                  Cancelar
                </button>
              </div>
            </section>
          )}
        </div>
      )}

      {confirmModal.isOpen && (
        <div className="app-modal-overlay">
          <div className="app-modal app-surface app-modal--info">
            <div className="app-modal-content">
              <h3 className="app-modal-title">Aviso Preventivo</h3>
              <p className="app-modal-message">
                Aviso: Debio haber enviado un SMS o Email al Arrendatario con 20 dias de anticipacion antes de ingresar estos reportes. Por favor, verifique la informacion antes de proceder.
              </p>
              <div className="app-actions app-modal-actions">
                <button type="button" className="app-button app-button--secondary" onClick={() => setConfirmModal({ isOpen: false })}>
                  Cancelar
                </button>
                <button type="button" className="app-button app-button--primary" onClick={executeSubmitAll}>
                  Entendido y Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pagoModal.isOpen && (
        <div className="app-modal-overlay">
          <div className="app-modal app-surface">
            <div className="app-modal-content">
              <h3 className="app-modal-title">Registrar Pago</h3>
              <form onSubmit={handleSubmitPago} autoComplete="off">
                <div className="app-field">
                  <label htmlFor="pago-valor">Valor Pagado</label>
                  <input
                    id="pago-valor"
                    type="number"
                    name="TRHValorPagado"
                    value={pagoModal.TRHValorPagado}
                    onChange={handlePagoChange}
                    step="0.01"
                    min="0"
                    max={pagoModal.saldo}
                    required
                    className="app-input"
                  />
                </div>
                <div className="app-field">
                  <label htmlFor="pago-fecha">Fecha de Pago (dd/mm/aaaa)</label>
                  <input
                    id="pago-fecha"
                    type="date"
                    name="TRHFechaPago"
                    value={pagoModal.TRHFechaPago}
                    onChange={handlePagoChange}
                    required
                    className="app-input"
                  />
                </div>
                <div className="app-field">
                  <label htmlFor="pago-obs">Observacion</label>
                  <textarea
                    id="pago-obs"
                    name="TRHPobservacion"
                    value={pagoModal.TRHPobservacion}
                    onChange={handlePagoChange}
                    rows="2"
                    className="app-textarea"
                  />
                </div>
                <div className="app-actions app-modal-actions">
                  <button type="button" className="app-button app-button--secondary" onClick={() => setPagoModal({ ...pagoModal, isOpen: false })}>
                    Cancelar
                  </button>
                  <button type="submit" disabled={submitting} className="app-button app-button--primary">
                    {submitting ? "Guardando..." : "Guardar Pago"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Didit para Verificación de Vida directamente en la aplicación */}
      {diditModalOpen && (
        <div className="app-modal-overlay" style={{ zIndex: 10000, padding: "16px" }}>
          <div
            className="app-modal app-surface"
            style={{
              maxWidth: "760px",
              width: "100%",
              maxHeight: "94vh",
              display: "flex",
              flexDirection: "column",
              borderRadius: "12px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
              overflow: "hidden",
            }}
          >
            {/* Encabezado del modal Didit */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                color: "#ffffff",
              }}
            >
              <div>
                <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#38bdf8", fontWeight: "700" }}>
                  Verificación de Vida e Identidad (Didit)
                </div>
                <h3 style={{ margin: "2px 0 0 0", fontSize: "1.15rem", color: "#ffffff", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>📸</span> Prueba Biométrica de Vida
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setDiditModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#94a3b8",
                  fontSize: "24px",
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: "4px 8px",
                }}
                aria-label="Cerrar"
              >
                &times;
              </button>
            </div>

            {/* Sub-barra informativa */}
            <div
              style={{
                padding: "8px 20px",
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                fontSize: "12px",
                color: "#475569",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div>
                <strong>Documento a verificar:</strong>{" "}
                <span style={{ color: "#0284c7", fontWeight: "700" }}>
                  {localStorage.getItem("user") || "Usuario Actual"}
                </span>
              </div>
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <span>
                  <strong>Clave API:</strong> <code>REFERENCIATE_BACKEND</code>
                </span>
              </div>
            </div>

            {/* Contenedor del Iframe Didit */}
            <div style={{ padding: "16px 20px", flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  padding: "10px 14px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "6px",
                  color: "#1e40af",
                  fontSize: "12px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>ℹ️</span>
                <span>
                  Permite el acceso a tu cámara para completar la prueba de vida facial y la captura de documento. La verificación se valida automáticamente con tu número de documento.
                </span>
              </div>

              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "560px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #cbd5e1",
                  background: "#000000",
                }}
              >
                <iframe
                  src={prerequisitos?.didit?.verification_url || "https://verify.didit.me/u/wqvI0FviQJCJeVLO81E_Hw"}
                  title="Verificación de Vida en Didit"
                  allow="camera; microphone; fullscreen; autoplay; encrypted-media; geolocation"
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              </div>
            </div>

            {/* Pie de modal con acciones */}
            <div
              style={{
                padding: "12px 20px",
                borderTop: "1px solid #e2e8f0",
                background: "#f8fafc",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <button
                type="button"
                onClick={() => window.open(prerequisitos?.didit?.verification_url || "https://verify.didit.me/u/wqvI0FviQJCJeVLO81E_Hw", "_blank")}
                style={{
                  background: "transparent",
                  border: "1px solid #cbd5e1",
                  color: "#475569",
                  borderRadius: "6px",
                  padding: "7px 12px",
                  fontSize: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                ↗️ Abrir en nueva ventana
              </button>

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  type="button"
                  className="app-button app-button--secondary"
                  onClick={() => setDiditModalOpen(false)}
                  style={{ fontSize: "13px" }}
                >
                  Cerrar
                </button>
                <button
                  type="button"
                  className="app-button app-button--primary"
                  onClick={() => handleVerificarDidit(false)}
                  disabled={validandoDidit}
                  style={{ fontSize: "13px" }}
                >
                  {validandoDidit ? "Sincronizando..." : "🔄 Sincronizar / Comprobar Estado"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AppModal 
        isOpen={modal.isOpen} 
        title={modal.title} 
        message={modal.message} 
        type={modal.type} 
        onClose={() => setModal({ ...modal, isOpen: false })} 
      />
    </AppShell>
  );
};

export default Reportar;


